import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import useCurrentLanguage from '~/hooks/use-current-language';
import {
  createDateFieldModel,
  isValidIsoCalendarDate,
  resolveDateMask,
  type DateFieldState,
} from '../dateMask.ts';
import { configuredAssetUrl, getTextFieldInputClassName } from './helpers.ts';
import defaultDatePickerAsset from '../assets/date_picker_button/default.svg';

type InputWithShowPicker = HTMLInputElement & {
  showPicker?: () => void;
};

function openNativeDatePicker(input: HTMLInputElement): void {
  const picker = input as InputWithShowPicker;
  if (typeof picker.showPicker !== 'function') {
    input.click();
    return;
  }

  try {
    picker.showPicker();
  } catch {
    input.click();
  }
}

export type DatePickerButtonAssets = {
  defaultAsset?: string | null;
  enableFocusedAsset?: boolean;
  focusedAsset?: string | null;
  enableErrorAsset?: boolean;
  errorAsset?: string | null;
};

type DateModeInputProps = {
  inputId: string;
  describedBy: string;
  hasError: boolean;
  required?: boolean;
  inputBorderStyle: string;
  additionInputStyle: CSSProperties | undefined;
  storedValue: string;
  invalidDateMessage: string;
  openCalendarLabel: string;
  datePickerAssets: DatePickerButtonAssets;
  onReportedChange: (reported: string) => void;
  onValidityChange: (error: string | undefined) => void;
};

/**
 * Picks the date picker icon. Error wins over focus, then the uploaded default,
 * then the bundled glyph.
 */
function resolveDatePickerAssetUrl({
  hasError,
  focused,
  assets,
}: {
  hasError: boolean;
  focused: boolean;
  assets: DatePickerButtonAssets;
}): string {
  const errorAssetUrl =
    hasError && assets.enableErrorAsset
      ? configuredAssetUrl(assets.errorAsset)
      : undefined;
  if (errorAssetUrl) return errorAssetUrl;

  const focusedAssetUrl =
    focused && assets.enableFocusedAsset
      ? configuredAssetUrl(assets.focusedAsset)
      : undefined;
  if (focusedAssetUrl) return focusedAssetUrl;

  return configuredAssetUrl(assets.defaultAsset) ?? defaultDatePickerAsset;
}

export function DateModeInput({
  inputId,
  describedBy,
  hasError,
  required,
  inputBorderStyle,
  additionInputStyle,
  storedValue,
  invalidDateMessage,
  openCalendarLabel,
  datePickerAssets,
  onReportedChange,
  onValidityChange,
}: DateModeInputProps): JSX.Element {
  const language = useCurrentLanguage();
  const dateInputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const model = useMemo(
    () => createDateFieldModel(resolveDateMask(language)),
    [language]
  );
  const [state, setState] = useState<DateFieldState>(() =>
    model.fromStored(storedValue)
  );

  useEffect(() => {
    setState(model.fromStored(storedValue));
  }, [model, storedValue]);

  const reportValidity = (reported: string): void => {
    if (!reported) {
      onValidityChange(undefined);
      return;
    }

    onValidityChange(
      isValidIsoCalendarDate(reported) ? undefined : invalidDateMessage
    );
  };

  const commit = (next: DateFieldState): void => {
    setState(next);
    if (next.reported !== storedValue) onReportedChange(next.reported);
    if (!next.reported || next.iso) reportValidity(next.reported);
  };

  const openNativePicker = (): void => {
    const nativeInput = dateInputRef.current;
    if (!nativeInput) return;
    openNativeDatePicker(nativeInput);
  };

  const datePickerAssetUrl = resolveDatePickerAssetUrl({
    hasError,
    focused,
    assets: datePickerAssets,
  });

  return (
    <div className="text-field-datepicker">
      <input
        id={inputId}
        type="text"
        inputMode="numeric"
        value={state.display}
        placeholder={model.placeholder}
        required={required}
        aria-invalid={hasError ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={getTextFieldInputClassName({
          inputBorderStyle,
          hasError,
        })}
        style={additionInputStyle}
        onChange={(event) => commit(model.accept(state, event.target.value))}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          reportValidity(state.reported);
        }}
      />
      <input
        ref={dateInputRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={state.iso}
        className="text-field-native-datepicker"
        onChange={(event) => {
          commit(model.fromStored(event.target.value));
        }}
      />
      <button
        type="button"
        className="text-field-datepicker-button"
        aria-label={openCalendarLabel}
        onClick={openNativePicker}
      >
        <img
          alt=""
          src={datePickerAssetUrl}
          className="h-4 w-4 object-contain"
        />
      </button>
    </div>
  );
}
